import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import DifferenceIcon from '@mui/icons-material/Difference';
import DownloadIcon from '@mui/icons-material/Download';
import ReplayIcon from '@mui/icons-material/Replay';
import CloseIcon from '@mui/icons-material/Close';
import { caseVersionAPI } from '../../services/api';
import { getCurrentUser } from '../../services/auth';
import { isGlobalAdmin, isTenantScopedAdmin, isVirtualMedicInstructor } from '../../utils/role';

const SNAPSHOT_LABELS = {
  'pre-update': 'Pre-Update',
  publish: 'Publish',
  unpublish: 'Unpublish',
  rollback: 'Rollback',
  manual: 'Manual'
};

const ENVIRONMENT_LABELS = {
  production: 'Production',
  testing: 'Testing',
  draft: 'Draft',
  null: 'N/A'
};

const formatDate = (value) => {
  if (!value) return 'Unknown';
  try {
    return new Date(value).toLocaleString();
  } catch (error) {
    return value;
  }
};

const summaryChipColor = (count) => (count > 0 ? 'primary' : 'default');

const VersionListItem = ({
  version,
  onViewDiff,
  onDownload,
  onRestore,
  restoring,
  disabled,
  showEnvironment = true
}) => {
  const summary = version.diffSummary || {};

  return (
    <ListItem
      alignItems="flex-start"
      sx={{
        borderRadius: 2,
        mb: 2,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper'
      }}
      secondaryAction={
        <Stack direction="row" spacing={1}>
          <Tooltip title="View diff">
            <span>
              <IconButton
                size="small"
                onClick={() => onViewDiff(version)}
                disabled={disabled}
              >
                <DifferenceIcon fontSize="inherit" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Download snapshot">
            <span>
              <IconButton
                size="small"
                onClick={() => onDownload(version)}
                disabled={disabled}
              >
                <DownloadIcon fontSize="inherit" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Restore case to this snapshot">
            <span>
              <IconButton
                size="small"
                color="primary"
                onClick={() => onRestore(version)}
                disabled={disabled || restoring}
              >
                <ReplayIcon fontSize="inherit" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      }
    >
      <ListItemText
        primary={
          <Box display="flex" alignItems="center" gap={1}>
            <HistoryIcon fontSize="small" color="action" />
            <Typography variant="subtitle1" component="span">
              Version {version.versionNumber}
            </Typography>
            <Chip
              label={SNAPSHOT_LABELS[version.snapshotType] || version.snapshotType}
              size="small"
              color={version.snapshotType === 'publish' ? 'success' : 'default'}
            />
            {showEnvironment && version.environment && (
              <Chip
                label={ENVIRONMENT_LABELS[version.environment] || version.environment}
                size="small"
                variant="outlined"
              />
            )}
          </Box>
        }
        secondary={
          <Stack spacing={1} mt={1}>
            <Typography variant="body2" color="text.secondary">
              {version.reason || 'No change description provided'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Captured {formatDate(version.createdAt)}
            </Typography>
            {version.triggeredBy && (
              <Typography variant="caption" color="text.secondary">
                By {version.triggeredBy.firstName} {version.triggeredBy.lastName} ({version.triggeredBy.email})
              </Typography>
            )}
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip
                size="small"
                label={`Fields ${summary.caseFieldsChanged || 0}`}
                color={summaryChipColor(summary.caseFieldsChanged)}
              />
              <Chip
                size="small"
                label={`Sections +${summary.sections?.added || 0}/-${summary.sections?.removed || 0}`}
                color={summaryChipColor((summary.sections?.added || 0) + (summary.sections?.removed || 0))}
              />
              <Chip
                size="small"
                label={`Sections Δ${summary.sections?.modified || 0}`}
                color={summaryChipColor(summary.sections?.modified)}
              />
              <Chip
                size="small"
                label={`Questions +${summary.examinerQuestions?.added || 0}/-${summary.examinerQuestions?.removed || 0}`}
                color={summaryChipColor((summary.examinerQuestions?.added || 0) + (summary.examinerQuestions?.removed || 0))}
              />
              <Chip
                size="small"
                label={`Questions Δ${summary.examinerQuestions?.modified || 0}`}
                color={summaryChipColor(summary.examinerQuestions?.modified)}
              />
              {summary.scoringOverridesChanged && (
                <Chip size="small" label="Scoring changed" color="warning" />
              )}
            </Stack>
          </Stack>
        }
      />
    </ListItem>
  );
};

const DiffDialog = ({ open, onClose, diffState, onBaselineChange }) => {
  const { loading, error, data, baseline } = diffState;
  const summary = data?.summary;
  const details = data?.details;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Snapshot Diff
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <ToggleButtonGroup
            value={baseline}
            exclusive
            size="small"
            onChange={(_, value) => {
              if (value) {
                onBaselineChange(value);
              }
            }}
          >
            <ToggleButton value="previous">Compare to Previous</ToggleButton>
            <ToggleButton value="current">Compare to Current</ToggleButton>
          </ToggleButtonGroup>

          {loading && (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress size={24} />
            </Box>
          )}

          {error && <Alert severity="error">{error}</Alert>}

          {data && !loading && (
            <Stack spacing={2}>
              {!summary?.hasBaseline && (
                <Alert severity="info">
                  No baseline snapshot was available. Showing the captured snapshot without comparison.
                </Alert>
              )}

              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Chip label={`Field changes: ${summary?.caseFieldsChanged || 0}`} />
                <Chip label={`Sections added: ${summary?.sections?.added || 0}`} />
                <Chip label={`Sections removed: ${summary?.sections?.removed || 0}`} />
                <Chip label={`Sections modified: ${summary?.sections?.modified || 0}`} />
                <Chip label={`Questions added: ${summary?.examinerQuestions?.added || 0}`} />
                <Chip label={`Questions removed: ${summary?.examinerQuestions?.removed || 0}`} />
                <Chip label={`Questions modified: ${summary?.examinerQuestions?.modified || 0}`} />
                {summary?.scoringOverridesChanged && <Chip color="warning" label="Scoring overrides changed" />}
              </Stack>

              <Divider textAlign="left">Case Fields</Divider>
              {details?.caseFieldChanges?.length ? (
                <List dense>
                  {details.caseFieldChanges.map(change => (
                    <ListItem key={change.field}>
                      <ListItemText
                        primary={change.field}
                        secondary={`From: ${change.from ?? '—'} → To: ${change.to ?? '—'}`}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">No top-level field changes.</Typography>
              )}

              <Divider textAlign="left">Sections</Divider>
              <Stack spacing={1}>
                <Typography variant="subtitle2">Added</Typography>
                {details?.sections?.added?.length ? (
                  <List dense>
                    {details.sections.added.map(section => (
                      <ListItem key={`added-${section.id || section.title}`}>
                        <ListItemText primary={section.title} secondary={`Order: ${section.order ?? '—'}`} />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary">No new sections.</Typography>
                )}

                <Typography variant="subtitle2">Removed</Typography>
                {details?.sections?.removed?.length ? (
                  <List dense>
                    {details.sections.removed.map(section => (
                      <ListItem key={`removed-${section.id || section.title}`}>
                        <ListItemText primary={section.title} secondary={`Order: ${section.order ?? '—'}`} />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary">No removed sections.</Typography>
                )}

                <Typography variant="subtitle2">Modified</Typography>
                {details?.sections?.modified?.length ? (
                  <List dense>
                    {details.sections.modified.map(section => (
                      <ListItem key={`modified-${section.id || section.title}`} alignItems="flex-start">
                        <ListItemText
                          primary={section.title}
                          secondary={
                            <Stack spacing={1}>
                              {section.sectionChanges?.length ? (
                                <Stack spacing={0.5}>
                                  {section.sectionChanges.map(change => (
                                    <Typography variant="body2" key={change.field}>
                                      {change.field}: {change.from ?? '—'} → {change.to ?? '—'}
                                    </Typography>
                                  ))}
                                </Stack>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  Section settings unchanged
                                </Typography>
                              )}
                              <Typography variant="subtitle2">Item changes</Typography>
                              {section.itemChanges?.added?.length ? (
                                <Typography variant="body2">
                                  + {section.itemChanges.added.length} new item(s)
                                </Typography>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  No new items
                                </Typography>
                              )}
                              {section.itemChanges?.removed?.length ? (
                                <Typography variant="body2">
                                  - {section.itemChanges.removed.length} item(s)
                                </Typography>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  No removed items
                                </Typography>
                              )}
                              {section.itemChanges?.modified?.length ? (
                                <Stack spacing={0.5}>
                                  {section.itemChanges.modified.map(item => (
                                    <Box key={`item-${item.id || item.title}`}>
                                      <Typography variant="body2" fontWeight={600}>{item.title}</Typography>
                                      {item.changes.map(change => (
                                        <Typography variant="body2" key={change.field}>
                                          {change.field}: {change.from ?? '—'} → {change.to ?? '—'}
                                        </Typography>
                                      ))}
                                    </Box>
                                  ))}
                                </Stack>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  No updated items
                                </Typography>
                              )}
                            </Stack>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary">No modified sections.</Typography>
                )}
              </Stack>

              <Divider textAlign="left">Examiner Questions</Divider>
              <Typography variant="subtitle2">Added</Typography>
              {details?.examinerQuestions?.added?.length ? (
                <List dense>
                  {details.examinerQuestions.added.map(question => (
                    <ListItem key={`q-added-${question.id || question.prompt}`}>
                      <ListItemText primary={question.prompt} />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">No new questions.</Typography>
              )}

              <Typography variant="subtitle2">Removed</Typography>
              {details?.examinerQuestions?.removed?.length ? (
                <List dense>
                  {details.examinerQuestions.removed.map(question => (
                    <ListItem key={`q-removed-${question.id || question.prompt}`}>
                      <ListItemText primary={question.prompt} />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">No removed questions.</Typography>
              )}

              <Typography variant="subtitle2">Modified</Typography>
              {details?.examinerQuestions?.modified?.length ? (
                <List dense>
                  {details.examinerQuestions.modified.map(question => (
                    <ListItem key={`q-mod-${question.id || question.prompt}`} alignItems="flex-start">
                      <ListItemText
                        primary={question.prompt}
                        secondary={
                          <Stack spacing={0.5}>
                            {question.changes.map(change => (
                              <Typography key={change.field} variant="body2">
                                {change.field}: {change.from ?? '—'} → {change.to ?? '—'}
                              </Typography>
                            ))}
                          </Stack>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">No modified questions.</Typography>
              )}

              <Divider textAlign="left">Scoring Overrides</Divider>
              {details?.scoringOverrideChanges?.length ? (
                <List dense>
                  {details.scoringOverrideChanges.map(change => (
                    <ListItem key={`score-${change.field}`}>
                      <ListItemText
                        primary={change.field}
                        secondary={`From: ${change.from ?? '—'} → To: ${change.to ?? '—'}`}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">No scoring override changes.</Typography>
              )}
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const CaseVersionTimeline = ({ caseId, caseTitle, onRestored }) => {
  const currentUser = useMemo(() => getCurrentUser(), []);
  const isPrivilegedUser = isTenantScopedAdmin(currentUser)
    || isVirtualMedicInstructor(currentUser)
    || isGlobalAdmin(currentUser);
  const isIndividualUser = currentUser?.accountType === 'individual' && !isPrivilegedUser;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [versions, setVersions] = useState([]);
  const [meta, setMeta] = useState({ totalVersions: 0 });
  const [typeFilters, setTypeFilters] = useState(new Set());
  const [environmentFilters, setEnvironmentFilters] = useState(new Set());
  const [restoringVersionId, setRestoringVersionId] = useState(null);
  const [actionMessage, setActionMessage] = useState({ type: '', text: '' });
  const [diffState, setDiffState] = useState({
    open: false,
    loading: false,
    error: '',
    data: null,
    baseline: 'previous',
    version: null
  });

  const loadVersions = useCallback(async () => {
    if (!caseId) {
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await caseVersionAPI.list(caseId, { limit: 50 });
      setVersions(response.data.versions || []);
      setMeta({ totalVersions: response.data.totalVersions || 0 });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load version history');
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => {
    loadVersions();
  }, [loadVersions]);

  const toggleFilter = (filterSet, value) => {
    const next = new Set(filterSet);
    if (next.has(value)) {
      next.delete(value);
    } else {
      next.add(value);
    }
    return next;
  };

  const filteredVersions = useMemo(() => {
    return versions.filter(version => {
      if (typeFilters.size > 0 && !typeFilters.has(version.snapshotType)) {
        return false;
      }
      if (environmentFilters.size > 0) {
        const envKey = version.environment || 'null';
        if (!environmentFilters.has(envKey)) {
          return false;
        }
      }
      return true;
    });
  }, [versions, typeFilters, environmentFilters]);

  const handleDownload = async (version) => {
    try {
      const response = await caseVersionAPI.downloadSnapshot(caseId, version.id);
      const blob = new Blob([response.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const safeTitle = (caseTitle || 'case').toLowerCase().replace(/[^a-z0-9]+/g, '-');
      link.href = url;
      link.download = `${safeTitle}-v${version.versionNumber}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setActionMessage({
        type: 'error',
        text: err.response?.data?.message || 'Failed to download snapshot'
      });
    }
  };

  const handleRestore = async (version) => {
    const confirmRestore = window.confirm(`Restore case to version ${version.versionNumber}? This will overwrite the draft case.`);
    if (!confirmRestore) {
      return;
    }

    try {
      setRestoringVersionId(version.id);
      await caseVersionAPI.restore(caseId, version.id, {});
      setActionMessage({
        type: 'success',
        text: `Case restored to version ${version.versionNumber}`
      });
      if (onRestored) {
        onRestored();
      }
      await loadVersions();
    } catch (err) {
      setActionMessage({
        type: 'error',
        text: err.response?.data?.message || 'Failed to restore case'
      });
    } finally {
      setRestoringVersionId(null);
    }
  };

  const openDiff = async (version, baseline = diffState.baseline) => {
    setDiffState({
      open: true,
      loading: true,
      error: '',
      data: null,
      baseline,
      version
    });
    try {
      const response = await caseVersionAPI.getDiff(caseId, version.id, { baseline });
      setDiffState(prev => ({
        ...prev,
        loading: false,
        data: response.data
      }));
    } catch (err) {
      setDiffState(prev => ({
        ...prev,
        loading: false,
        error: err.response?.data?.message || 'Failed to generate diff'
      }));
    }
  };

  const handleBaselineChange = async (baselineValue) => {
    if (!diffState.version) {
      return;
    }
    await openDiff(diffState.version, baselineValue);
  };

  return (
    <Box>
      <Stack spacing={2}>
        <Box>
          <Typography variant="h6">Version Timeline</Typography>
          <Typography variant="body2" color="text.secondary">
            {isIndividualUser
              ? 'Track case edits, publishes, and rollbacks.'
              : 'Track case edits, publishes, and rollbacks. Use filters to focus on specific environments or snapshot types.'}
          </Typography>
        </Box>

        {actionMessage.text && (
          <Alert
            severity={actionMessage.type === 'success' ? 'success' : 'error'}
            onClose={() => setActionMessage({ type: '', text: '' })}
          >
            {actionMessage.text}
          </Alert>
        )}

        <Stack direction="row" spacing={1} flexWrap="wrap">
          {Object.entries(SNAPSHOT_LABELS).map(([value, label]) => (
            <Chip
              key={value}
              label={label}
              variant={typeFilters.has(value) ? 'filled' : 'outlined'}
              color={typeFilters.has(value) ? 'primary' : 'default'}
              onClick={() => setTypeFilters(toggleFilter(typeFilters, value))}
              size="small"
            />
          ))}
        </Stack>

        {!isIndividualUser && (
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {Object.entries(ENVIRONMENT_LABELS).map(([value, label]) => (
              <Chip
                key={value}
                label={label}
                variant={environmentFilters.has(value) ? 'filled' : 'outlined'}
                color={environmentFilters.has(value) ? 'primary' : 'default'}
                onClick={() => setEnvironmentFilters(toggleFilter(environmentFilters, value))}
                size="small"
              />
            ))}
          </Stack>
        )}

        {loading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={24} />
          </Box>
        )}

        {error && !loading && <Alert severity="error">{error}</Alert>}

        {!loading && !error && filteredVersions.length === 0 && (
          <Alert severity="info">No snapshots match the selected filters.</Alert>
        )}

        {!loading && !error && filteredVersions.length > 0 && (
          <List disablePadding>
            {filteredVersions.map(version => (
              <VersionListItem
                key={version.id}
                version={version}
                onViewDiff={openDiff}
                onDownload={handleDownload}
                onRestore={handleRestore}
                restoring={restoringVersionId === version.id}
                disabled={Boolean(restoringVersionId && restoringVersionId !== version.id)}
                showEnvironment={!isIndividualUser}
              />
            ))}
          </List>
        )}

        <Typography variant="caption" color="text.secondary">
          Showing {filteredVersions.length} of {meta.totalVersions} snapshots.
        </Typography>
      </Stack>

      <DiffDialog
        open={diffState.open}
        onClose={() => setDiffState(prev => ({ ...prev, open: false }))}
        diffState={diffState}
        onBaselineChange={handleBaselineChange}
      />
    </Box>
  );
};

export default CaseVersionTimeline;
